import {
  CandidateSourceId,
  FormulaFunctionName,
  RecommendationEngineDiversityDimension,
  RecommendationEngineEndpoint,
  RecommendationEngineFilterKind,
  RecommendationEngineRerankKind,
} from "../../../types/recommendationEngine";

export const TOP_OF_PAGE_EXPLAINER =
  "This page controls how the recommendation engine chooses and ranks items for this experience. First it gathers possible items from the enabled candidate sources. Then it measures each item using signals. Then it applies the formula to produce a final score. Items with the highest scores appear first in the API response.";

export const MENTAL_MODEL_STEPS: string[] = [
  "Choose where items come from — candidate sources decide the pool of possible items.",
  "Choose what matters — signals measure useful qualities such as similarity, popularity, distance, or customer interest match.",
  "Choose how strongly each quality matters — the formula combines signal values into one final score.",
  "Preview before saving — preview shows which items would rank highest and why.",
  "Save when the result matches the business goal — saved changes affect live ranking.",
];

export const ENDPOINT_SUBTITLES: Record<RecommendationEngineEndpoint, string> = {
  similar: "Find items that most closely match the source item.",
  recommended:
    "Find items that go well with the source item based on shopping behavior and similarity.",
  nearby_picks: "Find strong items close to the shopper's location.",
  trending_now: "Find items that are currently gaining shopping activity.",
  browse_by_interest:
    "Find items that match the shopper's interests and favorite stores.",
  smart_picks:
    "Blend location, trend, and interest signals into one ranked result.",
};

export const HOW_THIS_WORKS_STEPS: string[] = [
  "Pick an experience — choose Similar, Recommended, Nearby Picks, Trending Now, Browse by Interest, or Smart Picks.",
  "Enable candidate sources — this decides where possible items come from.",
  "Review signals — signals describe what the engine can measure about each candidate.",
  "Adjust the formula — higher-weighted signals influence ranking more strongly.",
  "Preview results — inspect the returned items, their matched sources, and signal breakdowns.",
  "Refine if needed — if results feel too broad, tighten sources or filters; if too narrow, broaden the candidate pool.",
  "Save — publish the new configuration to the live recommendation engine.",
];

export type StepLayoutHeader = {
  title: string;
  helper: string;
};

export const STEP_LAYOUT_HEADERS: StepLayoutHeader[] = [
  {
    title: "Where items come from",
    helper:
      "Candidate sources decide which items enter the pool before ranking begins.",
  },
  {
    title: "Rules items must pass",
    helper:
      "Filters remove items that should never appear, no matter how high their score would be.",
  },
  {
    title: "How items are scored",
    helper:
      "The formula combines signal measurements into a single ranking score.",
  },
  {
    title: "How many items are returned",
    helper:
      "Controls how many items the engine ranks internally and how many are returned to the user.",
  },
  {
    title: "How the ranked list is polished",
    helper:
      "Rerank rules adjust the final ordering for variety or featured items.",
  },
  {
    title: "How scores are cleaned up",
    helper:
      "Post-processing reshapes the final score before it leaves the engine.",
  },
];

export const SECTION_LABELS: Record<string, string> = {
  "Candidate sources":
    "Where the recommendation engine is allowed to look for items",
  Signals: "What the recommendation engine measures about each item",
  Formula:
    "How the recommendation engine combines those measurements into one score",
  Filters: "Rules that must be true before an item can appear",
  "Rerank rules":
    "How the final ranked list is adjusted after scoring (variety, featured items)",
  Preview: "Test this configuration before saving",
};

export type CandidateSourceCopy = {
  plain: string;
  tooltip: string;
};

export const CANDIDATE_SOURCE_HELP: Record<
  CandidateSourceId,
  CandidateSourceCopy
> = {
  exact_name: {
    plain: "Finds items with the same cleaned-up product name.",
    tooltip:
      "Use this when exact text match is a strong indicator of relevance.",
  },
  image_ann: {
    plain: "Finds visually similar items using image embeddings.",
    tooltip:
      "Useful when products look alike even if names differ slightly.",
  },
  es_fuzzy: {
    plain:
      "Finds items with closely related names, even when the text is not identical.",
    tooltip:
      "Useful for spelling differences, alternate wording, or close title matches.",
  },
  shopping_list_cooccurrence: {
    plain:
      "Finds items that often appear in the same shopping lists as anchor items.",
    tooltip:
      "Use this to surface complementary items shoppers tend to add together.",
  },
  category_popular: {
    plain:
      "Finds items that are popular within the same category as the source item.",
    tooltip:
      "Good when you want safer, broad category-level recommendations.",
  },
  global_popular: {
    plain: "Finds items that are broadly popular across all shopping lists.",
    tooltip:
      "Use sparingly; this can bias results toward universally popular items.",
  },
  nearby_active: {
    plain: "Finds items available around the shopper's location.",
    tooltip: "Best when distance and local relevance matter.",
  },
  trending_items: {
    plain: "Finds items with recent shopping activity growth.",
    tooltip: "Use this to surface items that are currently gaining momentum.",
  },
  interest_category: {
    plain: "Finds items that match the shopper's preferred categories.",
    tooltip: "Good for category-driven personalization.",
  },
  interest_promotion_tag: {
    plain:
      "Finds items that match the shopper's preferred promotion themes or tags.",
    tooltip: "Useful for matching specific promotion styles or themes.",
  },
  promotion_tags: {
    plain: "Finds active deals carrying any of the promotion tags picked here.",
    tooltip:
      "Every shopper gets the same tagged deals, unlike interest promotion tags, which use the shopper's own tags.",
  },
  favorite_store: {
    plain: "Finds items from stores the shopper prefers.",
    tooltip: "Use this when store loyalty should influence ranking.",
  },
  source_category_match: {
    plain: "Finds active deals in the same categories as the source item.",
    tooltip: "Strengthens category-aligned recall on item detail pages.",
  },
  source_promotion_tag_match: {
    plain:
      "Finds active deals with the same promotion tags as the source item.",
    tooltip: "Use when promotion theme alignment matters.",
  },
  source_merchant_match: {
    plain: "Finds other deals from the same merchant as the source item.",
    tooltip:
      "Can increase same-retailer suggestions on Recommended results.",
  },
  source_flyer_list_match: {
    plain: "Finds other items on the same weekly flyer list as the source.",
    tooltip: "Surfaces sibling items from the same flyer.",
  },
  popular_on_shopping_lists: {
    plain:
      "Finds items that are broadly popular across shopping lists right now.",
    tooltip:
      "Good for cold-start coverage on section endpoints where no source item or shopper context is available.",
  },
  geo_popular_on_shopping_lists: {
    plain:
      "Finds items that are popular on shopping lists within the shopper's local radius.",
    tooltip:
      "Use when shopping-list popularity should be weighted by how close the activity is to the shopper.",
  },
  interest_category_popular: {
    plain:
      "Finds items that are popular within the shopper's preferred or requested interest categories.",
    tooltip:
      "Blends interest-driven personalization with shopping-list popularity.",
  },
};

export const CANDIDATE_SOURCE_FIELD_TOOLTIPS = {
  enabled:
    "Turn this source on if you want it to contribute candidate items to the pool.",
  cap:
    "The maximum number of items this source may contribute before scoring. Higher caps increase variety but may increase cost and noise.",
  sourceParams:
    "Advanced settings that narrow or expand how this source gathers candidates.",
  radiusKm:
    "The distance limit used when looking for nearby or geo-aware candidates.",
  hoursWindow:
    "How far back the engine looks when measuring recent trend activity.",
  maxMatches:
    "The maximum number of candidates to take from an interest-driven source.",
  anchorCap:
    "Maximum number of anchor items used when building co-occurrence recommendations.",
  anchorMinImageScore:
    "Only use image anchors that pass this minimum visual match strength.",
  onlyOnShoppingLists:
    "Restrict candidates to items that already appear on at least one shopping list.",
  minImageScore:
    "Only return image-similar items whose visual match strength is at or above this value.",
  useExactNameAnchors:
    "Use items found by exact-name match as anchors for co-occurrence lookups.",
  useImageAnchors:
    "Use items found by image similarity as anchors for co-occurrence lookups.",
  useEsFuzzyAnchors:
    "Use items found by fuzzy name match as anchors for co-occurrence lookups.",
  anchorsRequireOnShoppingLists:
    "Only build anchors from items that are already on a shopping list.",
};

export const FIELD_TOOLTIPS = {
  displayLimit:
    "The number of top-ranked items returned to the user after scoring is complete.",
  rankingWindowLimit:
    "The number of items the engine fully ranks before it cuts the list down for output. Higher values can improve quality but cost more work.",
  excludeSourceMerchant:
    "Remove items from the same merchant as the source item. Useful when Similar results should encourage variety.",
  distanceDecayRadius:
    "How quickly an item's score falls off with distance from the shopper, independent of each source's search radius. Must be greater than 0 and at most 100 km.",
  clamp01:
    "Force the final score into the 0 to 1 range. This makes scores easier to compare but can hide large differences.",
  round4dp:
    "Round the final score for cleaner display and stable logging.",
  formulaValidate:
    "Check whether the formula is structurally valid before preview or save.",
  preview:
    "Run the current draft configuration without saving it.",
  saveReason:
    "Optional note that explains why this configuration changed. This helps audit history remain understandable.",
  version:
    "Each successful save increases this number by one. The server uses it to make sure two admins do not overwrite each other's changes.",
  resetToDefault:
    "Restore the recommendation engine configuration for this endpoint to the server's default values.",
  discardChanges:
    "Throw away all unsaved edits and reload the saved configuration from the server.",
};

export const FORMULA_TOOLTIPS = {
  weight:
    "Controls how strongly this signal influences the final score. Larger values make the signal matter more.",
  operator:
    "Tells the formula how to combine two values, such as adding, subtracting, multiplying, or dividing.",
  literal:
    "A fixed number entered by the admin. Literals are commonly used as weights or thresholds.",
  fn:
    "A safe math helper such as min, max, or sqrt that reshapes a score without writing code.",
  functionArgs:
    "The values the function operates on, in order. Some functions need one argument and some need more than one.",
  rawWarning:
    "This signal is not normalized, so large values can overpower the rest of the formula.",
  clamp01Warning:
    "Final scores will be forced into the 0 to 1 range after the formula is evaluated.",
  combineWith:
    "Pick the single operator used to combine every term in the list. The same operator is applied between each pair.",
  term:
    "One row equals a weight multiplied by a signal. Each row is then combined with the others using the chosen operator.",
  binaryNode:
    "Combines two values using a single operator. Build up bigger expressions by nesting binary nodes.",
  literalNode:
    "A constant number that does not depend on the item.",
  signalNode:
    "A measurement the engine computes for each candidate item.",
  functionNode:
    "A safe math helper applied to one or more nested values.",
  textEditor:
    "Type the formula directly. Signals must be inserted from the picker and behave as locked pills.",
};

export const SIGNAL_CONCEPT_TOOLTIPS = {
  signal:
    "A measurement the recommendation engine calculates for each candidate item.",
  normalizedSignal:
    "A signal scaled into a small range, usually 0 to 1, so it is easier to combine with other signals.",
  rawSignal:
    "A direct count or unbounded value. Raw signals can dominate the formula if weights are too large.",
  applicableEndpoints:
    "The experiences where this signal is available and valid.",
  signalGroup: "A category used to help users browse signals more easily.",
  matchedSources:
    "The candidate sources that surfaced this item before scoring. An item may have several.",
  finalScore:
    "The score produced by the formula after post-processing. Higher scores rank first.",
  distinctSignals:
    "The number of unique signals your formula touches. Each one adds a small amount of compute cost.",
};

export const SIGNAL_GROUP_DESCRIPTIONS: Record<string, string> = {
  shopping_list:
    "Signals based on how often shoppers add items to shopping lists.",
  image: "Signals based on visual similarity between product images.",
  name: "Signals based on product name similarity.",
  candidate_source:
    "Signals that tell the formula how a candidate entered the pool.",
  category_merchant:
    "Signals about category or merchant relationships.",
  anchors:
    "Signals derived from anchor items used for co-occurrence style ranking.",
  similar_style: "Signals used specifically for the Similar experience.",
  item_attrs:
    "Signals based on product attributes such as item metadata.",
  geo: "Signals based on distance or location radius.",
  trend: "Signals based on recent activity and momentum.",
  interest:
    "Signals based on customer interests, tags, or favorite stores.",
};

export const FILTER_LABELS: Record<RecommendationEngineFilterKind, string> = {
  min_signal: "Minimum signal",
  require_on_shopping_list: "On shopping list",
  exclude_source_merchant: "Exclude source merchant",
  min_discount_percent: "Minimum discount",
  max_flyer_list_age_days: "Maximum flyer age",
  exclude_flyer_item_ids: "Blocked items",
  require_deals_page_eligible: "Deals page eligible",
  suppress_recently_served: "Suppress repeats",
};

export const FILTER_HELP: Record<RecommendationEngineFilterKind, string> = {
  min_signal:
    "Drop any candidate item whose value for the chosen signal is below the minimum. Useful for gating low-quality matches.",
  require_on_shopping_list:
    "Only keep items that already appear on at least one shopper's shopping list.",
  exclude_source_merchant:
    "Remove items that share the same merchant as the source item.",
  min_discount_percent:
    "Require at least this percent off the regular price before the item can appear.",
  max_flyer_list_age_days:
    "Drop items from flyer lists that started more than this many days ago.",
  exclude_flyer_item_ids:
    "Never show items whose IDs appear in this block list.",
  require_deals_page_eligible:
    "Only keep items that pass the consumer deals visibility rules.",
  suppress_recently_served:
    "Hide items recently shown in this section for the same shopper context. Higher values widen variety.",
};

export const RERANK_RULE_LABELS: Record<RecommendationEngineRerankKind, string> = {
  diversity_cap: "Diversity cap",
  boost_flyer_item_ids: "Boost featured items",
  demote_flyer_item_ids: "Demote items",
};

export const RERANK_RULE_HELP: Record<RecommendationEngineRerankKind, string> = {
  diversity_cap:
    "Limit how many top results share the same merchant, category, or store. Improves variety without changing scores.",
  boost_flyer_item_ids:
    "Multiply the final score for specific flyer item IDs to promote them. Use a multiplier greater than 1.",
  demote_flyer_item_ids:
    "Multiply the final score for specific flyer item IDs to demote them. Use a multiplier between 0 and 1.",
};

export const RERANK_FIELD_TOOLTIPS = {
  dimension:
    "Which property to group by when capping repeats — merchant, category, or store.",
  maxPerGroup:
    "Maximum number of items allowed from the same group before later items in the same group are skipped.",
  multiplier:
    "The factor applied to the final score for matching items. Greater than 1 promotes; between 0 and 1 demotes.",
  flyerItemIds:
    "Comma-separated flyer item UUIDs the rule applies to.",
};

export const DIVERSITY_DIMENSION_LABELS: Record<
  RecommendationEngineDiversityDimension,
  string
> = {
  merchant: "Merchant",
  category: "Category",
  store: "Store",
};

export const RERANK_RULES_INTRO =
  "Rerank rules adjust the ranked list after scoring. They run on the ranked window (up to the ranking window limit), not only the visible page.";

export const SELECTION_GUIDANCE_BULLETS: string[] = [
  "Broader pool: enable more sources and raise caps.",
  "More precise pool: disable weak sources and lower caps.",
  "Safer results: prefer exact name, category popularity, favorite store.",
  "Discovery-oriented results: prefer trending items, global popularity, fuzzy discovery.",
  "Location-focused results: prefer nearby active items and distance-based signals.",
  "Personalized results: prefer interest categories, promotion tags, and favorite stores.",
];

export const VALIDATION_MESSAGE_TEMPLATES = {
  successHeader: "Formula is valid.",
  failureHeader: "Formula has errors.",
  signalsLine: (signalIds: string[]) =>
    signalIds.length === 0
      ? "Signals: none (0 distinct)."
      : `Signals: ${signalIds.join(", ")} (${signalIds.length} distinct).`,
  rawWarningLine: (raw: string[]) =>
    `Raw signal${raw.length === 1 ? "" : "s"} ${raw.join(
      ", ",
    )} may dominate when clamp 0-1 is enabled.`,
};

export const PREVIEW_FIELD_TOOLTIPS = {
  sourceFlyerItemId:
    "The flyer item used as the source for Similar and Recommended preview runs.",
  customerId:
    "Pretend the preview is being requested by this customer. Used to derive interests, favorite stores, and shopping-list context.",
  categoryIds:
    "Comma-separated category IDs. Restricts interest-driven previews to these categories.",
  promotionTagIds:
    "Comma-separated promotion tag IDs. Restricts interest-driven previews to these tags.",
  favoriteStoreIds:
    "Comma-separated favorite store IDs. Restricts interest-driven previews to these stores.",
  latitude:
    "Pretend the shopper is at this latitude. Used by Nearby Picks, Trending Now, and Smart Picks previews.",
  longitude:
    "Pretend the shopper is at this longitude. Used by Nearby Picks, Trending Now, and Smart Picks previews.",
  radiusKm:
    "Override the radius (in kilometers) the engine searches around the shopper for this preview.",
  hoursWindow:
    "Override the trend look-back window (in hours) for this preview.",
  limit:
    "How many ranked items to display in the preview table. Capped server-side at 20.",
  matchedSourcesChip:
    "Which candidate sources surfaced this item. Hover a chip to see how it entered the pool.",
};

export const FUNCTION_DESCRIPTIONS: Record<FormulaFunctionName, string> = {
  min: "Returns the smallest of two or more values.",
  max: "Returns the largest of two or more values.",
  abs: "Returns the value without its sign — always zero or positive.",
  pow: "Raises the first value to the power of the second.",
  sqrt: "Returns the square root. The input must be zero or positive.",
  log: "Returns the natural logarithm. The input must be greater than zero.",
  log10: "Returns the base-10 logarithm. The input must be greater than zero.",
  exp: "Returns e raised to the value's power.",
  floor: "Rounds the value down to the nearest whole number.",
  ceil: "Rounds the value up to the nearest whole number.",
  round: "Rounds the value to the nearest whole number.",
};

export const GLOBAL_SETTINGS_TOOLTIPS = {
  literalAbsMax:
    "A safety cap for literal numbers used inside formulas. The engine rejects any formula whose literal exceeds this absolute value, helping prevent runaway weights.",
  refreshCityCache:
    "Queue background jobs that rebuild the city-fallback cache for section endpoints. Use when configuration changes should be reflected for shoppers without a personalized warm-set entry.",
  refreshWarmSetCache:
    "Queue background jobs that rebuild personalized customer caches for shoppers in the warm set. Use after a configuration change so returning shoppers see the new ranking.",
  cacheRefreshEndpoints:
    "Limit the refresh to specific section endpoints. Leave empty to refresh all section endpoints (Nearby Picks, Trending Now, Browse by Interest, Smart Picks).",
  cacheRefreshCityIds:
    "Comma-separated city UUIDs to refresh. Leave empty to refresh every enabled city.",
  cacheRefreshCustomerIds:
    "Comma-separated customer UUIDs to refresh. Leave empty to refresh all warm-set customers.",
};
